import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Toggle } from 'stories/components'

/** What a borrower statement asks for beyond the period it bills. */
export interface StatementDefaultsConfig {
  /** Bill interest that was billed on a payment and not collected. */
  billUnpaidInterest: boolean
  /** Bill the loan's other outstanding charges, each at balance plus the interest standing on it. */
  billUnpaidCharges: boolean
}

/**
 * Two balances a borrower statement did not ask for.
 *
 * A statement bills the installment(s) the loan owes, its unpaid late charges and the Other
 * Payments configured on Servicing Information. Interest that was billed and not collected, and
 * the loan's other outstanding charges, were left out of the figure - the charges were even
 * itemised at the end of the page under a total that ignored them.
 *
 * Both switches start off, and saving nothing changes nothing: turning one on asks every borrower
 * on the book for money last month's statement did not, which is this servicer's decision rather
 * than something to happen to them on a deploy.
 *
 * Separate switches because the two are different kinds of money - a servicer may well bill the
 * insurance it advanced while leaving uncollected interest to be settled at payoff.
 */
export const StatementDefaultsConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [billUnpaidInterest, setBillUnpaidInterest] = useState(false)
  const [billUnpaidCharges, setBillUnpaidCharges] = useState(false)

  useEffect(() => {
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    setLoading(true)
    try {
      const data = await getAdminConfig('statementDefaults')
      // Only an explicit true turns a switch on, matching the server: a tenant that has never
      // saved this screen reads as both off rather than as a deliberate save.
      setBillUnpaidInterest(data?.billUnpaidInterest === true)
      setBillUnpaidCharges(data?.billUnpaidCharges === true)
    } catch (error) {
      console.error('Error fetching statement defaults:', error)
    } finally {
      setLoading(false)
    }
  }

  const onSave = async () => {
    setLoading(true)
    try {
      await setAdminConfig('statementDefaults', { billUnpaidInterest, billUnpaidCharges })
      toast('Statement defaults saved.', { type: 'success' })
    } catch (error) {
      console.error('Error saving statement defaults:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Statement Defaults</h2>
        <p className="text-sm text-gray-600 mt-1">
          What a borrower statement asks for beyond the period it covers. A statement always bills the installment it
          owes, its unpaid late charges and the Other Payments set on Servicing Information; these two switches decide
          whether it also bills the balances the loan is carrying.
        </p>
      </div>

      {/* Non-Dutch only, which is where a servicer bills rather than letting the balance compound:
          construction, a revolving line, anything accruing on a daily balance. A Dutch loan adds an
          interest shortfall to the balance it accrues on, so it comes back through the next
          period's interest and a line of its own would charge it twice. */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6 max-w-3xl">
        <div className="font-medium text-gray-800 mb-1">Carried Balances</div>
        <p className="text-sm text-gray-600 mb-3">
          Applies to <span className="font-medium">non-Dutch loans only</span> - construction, lines of credit, and
          anything else billed on a daily balance. A Dutch loan adds uncollected interest to the balance it accrues on,
          so billing it again here would charge the borrower twice. Each amount billed is stated on its own line of the
          statement, so nothing arrives as part of a figure the borrower cannot place.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Toggle
            id="statement-bill-unpaid-interest"
            title="Bill interest that was billed and not collected"
            value={billUnpaidInterest}
            onChange={(value: boolean) => setBillUnpaidInterest(value)}
          />
          <Toggle
            id="statement-bill-unpaid-charges"
            title="Bill the loan's outstanding charges"
            value={billUnpaidCharges}
            onChange={(value: boolean) => setBillUnpaidCharges(value)}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Outstanding charges are billed at what each is worth today - its balance plus the interest standing on it -
          which is the same figure the statement already itemises at the end of the page and the Servicing screen
          reports as Unpaid Charges.
        </p>
      </div>

      <Button onClick={onSave}>Save</Button>
    </div>
  )
}
